import { Injectable, inject } from '@angular/core';

import { translate } from '@jsverse/transloco';

import {
  BigNumberSummaryParameters,
  BreakdownSummaryParameters,
  CrosstabSummaryParameters,
  DonutSummaryParameters,
  GaugeSummaryParameters,
  ImpactSummaryParameters,
  NumericMetadataSummaryParameters,
  ScatterPlotSummaryParameters,
  SentimentPolaritySummaryParameters,
  SentimentScoreSummaryParameters,
  SentimentVsVolumeSummaryParameters,
  VolumeOverTimeSummaryParameters,
  VolumeSummaryParameters,
} from '.';
import { LexDatePipe } from '../../../../../../../pipes';
import { Widget } from '../../../../../../../providers/bridge-api/dashboard.service';
import { EnableClassTelemetry } from '../../../../../../../services/otel.service';
import { ChartOptionUtils } from '../../../../../../../utils/chart-options.utils';
import { WidgetChartOptions } from '../../../../widget-chart/widget-chart.component';

export interface AiSummaryTranslocoParameters {
  assembleParameters(selectedPoints: any[], widget: Widget<WidgetChartOptions>);
}

export interface TranslocoEntry {
  translationKey: string;
  translationParams: { [key: string]: any };
}

@EnableClassTelemetry()
@Injectable({ providedIn: 'root' })
export class WidgetAiInsightsSummaryService {
  datePipe = inject(LexDatePipe);

  generateInsightSummary(selectedPoints: any[], widget: Widget<WidgetChartOptions>) {
    const parameterAssembler = this.createParameterAssembler(widget);
    return this.assembleSummary(parameterAssembler.assembleParameters(selectedPoints, widget));
  }

  private createParameterAssembler(
    widget: Widget<WidgetChartOptions>
  ): AiSummaryTranslocoParameters {
    switch (widget.payload.type) {
      case 'donut':
        return new DonutSummaryParameters();
      case 'documentCount':
        if (ChartOptionUtils.isOverTimeAggregator(widget.payload.aggregator)) {
          return new VolumeOverTimeSummaryParameters(this.datePipe);
        }
        return new VolumeSummaryParameters();
      case 'numericMetadata':
        return new NumericMetadataSummaryParameters(this.datePipe);
      case 'breakdown':
        return new BreakdownSummaryParameters();
      case 'gauge':
        return new GaugeSummaryParameters();
      case 'bigNumber':
        return new BigNumberSummaryParameters();
      case 'sentimentPolarity':
        return new SentimentPolaritySummaryParameters();
      case 'sentimentScore':
        return new SentimentScoreSummaryParameters(this.datePipe);
      case 'scatterPlot':
        return new ScatterPlotSummaryParameters();
      case 'sentimentVolume':
        return new SentimentVsVolumeSummaryParameters(this.datePipe);
      case 'impact':
        return new ImpactSummaryParameters();
      case 'crosstab':
        return new CrosstabSummaryParameters();
    }
  }

  private assembleSummary(translationEntries: TranslocoEntry[]) {
    const summaryStringParts = [];

    for (let i = 0; i < translationEntries.length; i++) {
      const entry = translationEntries[i];

      let translatedString = translate(entry.translationKey, entry.translationParams);
      if (i !== 0) {
        translatedString = this.lowercaseFirstLetter(translatedString);
      }
      summaryStringParts.push(translatedString);
    }

    return summaryStringParts.join(', ');
  }

  private lowercaseFirstLetter(text: string) {
    if (!text) {
      return '';
    }
    return text[0].toLocaleLowerCase() + text.slice(1);
  }
}
